import React, { useState, useEffect } from 'react';

import { isChromeUsingOpenGL, getChromeGraphicsFixInstructions } from '../../utils/webglDetection';

const LOCAL_STORAGE_KEY = 'webgl-warning-dismissed';

interface WebGLWarningBannerProps {
  /** Override visibility check with an explicit value */
  forceShow?: boolean;
  /** Callback when the fix button is clicked */
  onFixClick?: () => void;
  /** Callback when the dismiss button is clicked */
  onDismiss?: () => void;
  /** CSS class to apply to the container */
  className?: string;
  /** Additional styles */
  style?: React.CSSProperties;
}

/**
 * Banner component that warns users when Chrome is using the OpenGL backend
 * which can cause performance issues with 3D graphics
 */
const WebGLWarningBanner: React.FC<WebGLWarningBannerProps> = ({
  forceShow = false,
  onFixClick,
  onDismiss,
  className = '',
  style = {}
}) => {
  // Check if warning was previously dismissed
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return localStorage.getItem(LOCAL_STORAGE_KEY) === 'true';
    } catch (e) {
      return false;
    }
  });

  // Check if the warning should be shown
  const [shouldShow, setShouldShow] = useState(false);
  
  // Instructions for fixing the issue
  const instructions = getChromeGraphicsFixInstructions();
  
  // Check if we're using Chrome with OpenGL backend and set visibility
  useEffect(() => {
    if (forceShow) {
      setShouldShow(true);
      return;
    }

    const hasProblem = isChromeUsingOpenGL();
    setShouldShow(hasProblem && !isDismissed);
  }, [forceShow, isDismissed]);

  // Handle dismiss button click
  const handleDismiss = () => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    } catch (e) {
      console.warn('Could not save dismissal state to localStorage', e);
    }
    
    setIsDismissed(true);
    
    if (onDismiss) {
      onDismiss();
    }
  };

  // Handle fix button click
  const handleFixClick = () => {
    try {
      // Try to open the chrome://flags page (may fail due to browser security)
      window.open(instructions.url, '_blank');
    } catch (e) {
      console.warn('Could not open chrome://flags directly, please copy and paste the URL', e);
      // Copy the URL to clipboard as fallback
      navigator.clipboard.writeText(instructions.url).catch(() => {
        console.warn('Could not copy to clipboard');
      });
    }
    
    if (onFixClick) {
      onFixClick();
    }
  };
  
  if (!shouldShow) {
    return null;
  }

  return (
    <div 
      className={`fixed top-0 left-0 right-0 bg-yellow-100 border-b border-yellow-300 p-4 z-50 ${className}`}
      style={{ ...style }}
      data-testid="webgl-warning-banner"
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
          <div className="flex-1 mr-4">
            <h3 className="text-yellow-800 font-bold text-lg">
              {instructions.title}
            </h3>
            <p className="text-yellow-700 mb-2">
              {instructions.description}
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-yellow-800 text-sm mb-2 sm:mb-0">
              <ol className="list-decimal list-inside">
                {instructions.instructions.map((step, index) => (
                  <li key={index} className="mb-1 last:mb-0">{step}</li>
                ))}
              </ol>
            </div>
          </div>
          
          <div className="flex flex-row sm:flex-col space-x-3 sm:space-x-0 sm:space-y-2">
            <button 
              onClick={handleFixClick}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded transition-colors"
            >
              Open Settings
            </button>
            <button 
              onClick={handleDismiss}
              className="bg-transparent hover:bg-yellow-200 text-yellow-700 py-2 px-4 rounded transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
        
        {instructions.note && (
          <p className="text-yellow-600 text-xs mt-2">{instructions.note}</p>
        )}
      </div>
    </div>
  );
};

export default WebGLWarningBanner; 
